import { type LucideIcon, X } from 'lucide-react'
import {
  cloneElement,
  type HTMLAttributes,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
  useId,
} from 'react'
import {
  RETURNED_CHECKOUT_NOTICE,
  type ReturnedCheckout,
} from '../lib/returned-checkout'
import { InfoTip } from './InfoTip'
import { Tooltip } from './Tooltip'
import { useAutoGrow } from './useAutoGrow'
import { useCopy } from './useCopy'
import './ui.css'
import './badge.css'
import './card.css'
import './date-input.css'
import './field.css'
import './notice.css'
import './toggle.css'

/**
 * ShulBase UI primitives — the shared vocabulary every screen is built from.
 *
 * Two rules keep this coherent as it grows:
 *   1. Components style themselves with SEMANTIC tokens only. No raw colors,
 *      no one-off hex values, so the dark theme needs no changes here.
 *   2. Variants are props, not new components. `<Button variant="quiet">`,
 *      never `<QuietButton>`.
 */

/**
 * A labeled control. The label is bound to its input with an explicit id
 * rather than by wrapping — implicit association is valid HTML but explicit
 * survives refactors and is what screen readers handle most consistently.
 *
 * The label NAMES the control and does nothing else — two or three words, no
 * parenthetical, no sentence, no example. A label is not an information center:
 * you read a column of them to find the field you want, and prose in that column
 * defeats the scan it exists for.
 *
 * Everything that explains, warns or gives an example goes in `info`, which
 * rides beside the label as a ⓘ and opens on click. It costs the form no height,
 * so a field that needs a paragraph and a field that needs nothing are the same
 * size — which is what lets a row of them line up.
 *
 * `optional` is the one exception to that, and it is an AFFORDANCE rather than
 * prose: whether a field may be left empty is read BEFORE anything is typed, so
 * it cannot sit behind a click the way an explanation can. It is a marker beside
 * the label, not a word in it — the label still only names the control, and no
 * caller writes a parenthetical. It sits inside the `<label>` so it is part of
 * the accessible name: a marker only a sighted reader gets is the same defect as
 * a description nothing announces.
 *
 * Marking a field says the server accepts it absent — check that before setting
 * it. A marker that lies is worse than none, because its absence elsewhere is
 * what tells a reader the rest are required.
 *
 * `span="full"` makes it take the whole width of a two-column FieldRow — for the
 * one long field (a name, a textarea) that sits among paired short ones.
 */
export function Field({
  label,
  info,
  optional,
  token,
  span,
  problem,
  children,
}: {
  label: string
  /** The explanation, one click away. Never fold this into the label. */
  info?: ReactNode
  /** This may be left empty, and the server takes it that way. */
  optional?: boolean
  /**
   * The merge token this field's value fills, without braces (`zelle`). Shown
   * beside the label, so the token is learned where the value is set.
   */
  token?: string
  span?: 'full'
  /**
   * What is wrong with the value, as a sentence saying what to do — shown under
   * the control, which is marked invalid and described by it. Absent while the
   * value is fine or has not been left yet.
   */
  problem?: string | null
  children: ReactNode
}) {
  const id = useId()
  const problemId = `${id}-problem`
  const control = isValidElement(children)
    ? cloneElement(
        children as ReactElement<{
          id?: string
          'aria-invalid'?: boolean
          'aria-describedby'?: string
        }>,
        problem
          ? { id, 'aria-invalid': true, 'aria-describedby': problemId }
          : { id },
      )
    : children
  return (
    <div className={`field${span === 'full' ? ' field--full' : ''}`}>
      <span className="field__labelrow">
        <label className="field__label" htmlFor={id}>
          {label}
          {optional ? <span className="field__optional">Optional</span> : null}
        </label>
        {info ? (
          <InfoTip size="inline" label={`About ${label}`}>
            {info}
          </InfoTip>
        ) : null}
        {token ? <FieldToken token={token} /> : null}
      </span>
      {control}
      {problem ? (
        <p id={problemId} className="field__problem">
          {problem}
        </p>
      ) : null}
    </div>
  )
}

/** The token chip: click copies it, to paste straight into a template. */
function FieldToken({ token }: { token: string }) {
  const [copied, copy] = useCopy()
  const text = `{{${token}}}`
  return (
    <Tooltip label={copied ? 'Copied' : `Copy ${text}`}>
      <button type="button" className="field__token" onClick={() => copy(text)}>
        {copied ? 'copied' : text}
      </button>
    </Tooltip>
  )
}

/**
 * One row of a form: one field or two, never three.
 *
 * The cap is the point. Fields laid out by whatever happens to fit produce rows
 * of two, then one, then three, and the eye reads the ragged left edge of the
 * controls rather than the labels. Two columns of equal width, collapsing to one
 * when there isn't room for two, means every control in a form lines up with the
 * one above it and every field is one of exactly two widths.
 */
export function FieldRow({
  cols = 2,
  children,
}: {
  cols?: 1 | 2
  children: ReactNode
}) {
  return (
    <div className="field-row" data-cols={cols}>
      {children}
    </div>
  )
}

/**
 * A setting that isn't a field: a title, a sentence saying what it changes, and
 * the control that changes it — inside its own bordered block.
 *
 * The border is doing real work. A toggle dropped into the field column reads as
 * a value you fill in, and its label has to carry the consequence in a
 * parenthetical because there is nowhere else to put it. A setting is a
 * statement you act on, so it gets room to say what acting on it does.
 *
 * The description is the consequence of acting, so the control CARRIES it: it
 * is given an id and handed to the child as `aria-describedby`, which is what
 * makes a screen reader say what the switch does rather than only that it is a
 * switch. A caller's own `aria-describedby` is kept and this one appended.
 */
export function SettingBlock({
  title,
  description,
  className,
  detail,
  info,
  children,
}: {
  title: string
  /** What changes when this is turned on, read in place. The reason the
   *  block exists, on a block whose consequence somebody must read before
   *  flipping it. */
  description?: ReactNode
  /** The same reason behind an ⓘ, for a block whose title already says
   *  enough to act on and whose explanation is there for whoever asks. A
   *  column of such blocks reads as a list of switches rather than as prose. */
  info?: ReactNode
  /** Where a screen has to place the block against its own layout. */
  className?: string
  /**
   * The choice a setting opens once it is on — which drive, which luach. It
   * belongs inside the block rather than as a field after it, so that turning
   * the setting off takes the question away with it.
   */
  detail?: ReactNode
  children: ReactNode
}) {
  const descId = useId()
  const control =
    description && isValidElement<{ 'aria-describedby'?: string }>(children)
      ? cloneElement(children, {
          'aria-describedby': [children.props['aria-describedby'], descId]
            .filter(Boolean)
            .join(' '),
        })
      : children
  return (
    <div
      className={className ? `setting-block ${className}` : 'setting-block'}
      data-compact={description ? undefined : ''}
    >
      <div className="setting-block__row">
        <div className="setting-block__text">
          <span className="setting-block__title">
            {title}
            {info ? (
              <InfoTip size="inline" label={`About ${title}`}>
                {info}
              </InfoTip>
            ) : null}
          </span>
          {description ? (
            <p className="setting-block__desc" id={descId}>
              {description}
            </p>
          ) : null}
        </div>
        <div className="setting-block__control">{control}</div>
      </div>
      {detail ? <div className="setting-block__detail">{detail}</div> : null}
    </div>
  )
}

/**
 * A titled group of fields. The rule above it and the space around it are what
 * make a long form scannable — a stack of undifferentiated fields is a list you
 * read from the top every time, rather than a set of areas you can jump between.
 *
 * `description` is for the group as a whole; a note about one control is that
 * control's `hint`.
 */
export function FieldSection({
  title,
  description,
  children,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="field-section">
      <div className="field-section__head">
        <h3 className="field-section__title">{title}</h3>
        {description ? (
          <p className="field-section__desc">{description}</p>
        ) : null}
      </div>
      <div className="field-section__body">{children}</div>
    </section>
  )
}

/**
 * A calendar day. `variant` is appearance: a field fills its grid cell under
 * the `Field` that names it; a tool sits in a list header beside the other
 * tools, at their height and edge, and names itself in place with `label` —
 * the word a header's bare controls carry instead of a label above them.
 */
export function DateInput({
  className,
  variant = 'field',
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> &
  ({ variant?: 'field'; label?: never } | { variant: 'tool'; label: string })) {
  if (variant === 'tool') {
    /* The label wraps the input, so the word is its accessible name and a
       click on it reaches the control. */
    return (
      <label className={`tool date-tool ${className ?? ''}`}>
        <span className="date-tool__name">{label}</span>
        <input type="date" className="date-tool__input" {...rest} />
      </label>
    )
  }
  return (
    <input type="date" className={`control ${className ?? ''}`} {...rest} />
  )
}

/** A plain text/number input on the design system's `control` styling. */
export function TextInput({
  className,
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input className={`control ${className ?? ''}`} ref={ref} {...rest} />
}

/**
 * Multiline text. The one textarea — vertical-resize, token min-height.
 *
 * `grow` makes the box track what is written in it, for somewhere a person
 * writes a sentence and keeps going. It is opt-in because `rows` is the right
 * answer wherever the box's size is the surface's decision rather than the
 * text's — an editor that owns its pane, a field in a form whose rows line up
 * with the fields beside it.
 */
export function Textarea({
  className,
  grow = false,
  ref,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  grow?: boolean
  ref?: Ref<HTMLTextAreaElement>
}) {
  const attach = useAutoGrow(grow, ref)
  return (
    <textarea
      className={`control textarea ${className ?? ''}`}
      data-grow={grow ? '' : undefined}
      ref={attach}
      {...rest}
    />
  )
}

/**
 * A padded, bordered surface — the one card chrome, for panels and record cards.
 * `raised` (default) lifts above the page; `sunken` sits on it; `filled` takes
 * the faint fill that sets a panel off the page it sits on — a form, or a
 * statement about what surrounds it. The body is a token-gapped grid.
 *
 * `title` heads the card on a hairline, and `action` is its one way further —
 * the page it summarizes, or the act it offers — at the head's trailing edge.
 * `icon` sits before the title and says what kind of thing the card is, so a
 * grid of them is scanned by shape; a titled card usually carries one.
 * A card on an overview is titled; a record card in a list is not.
 */
export function Card({
  tone = 'raised',
  as: As = 'div',
  title,
  icon: Icon,
  action,
  className,
  children,
  ...rest
}: Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  tone?: 'raised' | 'sunken' | 'filled'
  as?: 'div' | 'article' | 'section'
  title?: ReactNode
  icon?: LucideIcon
  action?: ReactNode
}) {
  return (
    <As className={`card card--${tone} ${className ?? ''}`} {...rest}>
      {title ? (
        <div className="card__head">
          <h2 className="card__title">
            {Icon ? <Icon size={16} aria-hidden="true" /> : null}
            {title}
          </h2>
          {action ? <div className="card__action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </As>
  )
}

/**
 * A boolean, as a labeled switch. The checkbox is the source of truth; the
 * track is its visual stand-in, so it stays keyboard-operable.
 *
 * The label names WHAT the switch controls; the switch itself shows whether it
 * is on. A label that also says "on"/"off" states the same fact twice and the
 * two can visibly disagree mid-flip, so don't write the state into it.
 * `hideLabel` drops it from view — for a table column whose header already
 * names it — while keeping it for screen readers.
 */
export function Toggle({
  checked,
  onChange,
  label,
  hideLabel,
  disabled,
  'aria-describedby': describedBy,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hideLabel?: boolean
  /** Not yours to change — the state still reads, the control does not respond. */
  disabled?: boolean
  /**
   * The sentence saying what flipping this does, where a surrounding block
   * states one. It goes on the input rather than the wrapping label, so it is
   * the control's DESCRIPTION and not part of its name.
   */
  'aria-describedby'?: string
}) {
  return (
    <label
      className="toggle"
      data-active={checked ? '' : undefined}
      data-disabled={disabled ? '' : undefined}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle__track" aria-hidden>
        <span className="toggle__thumb" />
      </span>
      <span className={hideLabel ? 'sr-only' : 'toggle__label'}>{label}</span>
    </label>
  )
}

/**
 * A status chip.
 *
 * The default is MONOCHROME, and that is the rule rather than a fallback: a
 * state does not earn a color by existing. "Draft", "Sent", "Paid up", a count
 * — all of them are muted text on a faint fill. The default is `neutral` so
 * that a badge nobody has thought about comes out gray rather than wearing a
 * color reserved for something else.
 *
 * A tone is a state's MEANING, chosen the same way on every screen: `success`
 * at rest and in order, `info` scheduled or a rehearsal, `progress` underway —
 * a live environment — and `warning` needing care, never beside a gold mark,
 * whose hue it shares. `danger` is real failure.
 * `review` is the tracker, and nothing else.
 */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?:
    | 'neutral'
    | 'review'
    | 'danger'
    | 'success'
    | 'info'
    | 'progress'
    | 'warning'
  children: ReactNode
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

/**
 * A standing note. `info` and `error` are boxed — they interrupt. `note` is
 * fenced instead: a rule down its leading edge and no box, for a paragraph that
 * belongs to the form it sits in rather than to the page.
 *
 * A failure ANNOUNCES itself. An error notice appears where a commit just went
 * wrong, and a reader who cannot see it arriving is left with a button that did
 * nothing and no sentence saying why — so `error` carries `role="alert"` and the
 * other tones, which are standing text rather than news, carry none.
 */
export function Notice({
  tone = 'info',
  icon: Icon,
  compact = false,
  children,
  onDismiss,
}: {
  tone?: 'info' | 'error' | 'note'
  /** What the notice is about, as a mark leading the text. */
  icon?: LucideIcon
  /** One line's worth of inset, for a notice standing over a document. */
  compact?: boolean
  children: ReactNode
  /**
   * A notice that stands until its reader has read it — said on arrival
   * somewhere it would otherwise stay for good — carries its own way out.
   */
  onDismiss?: () => void
}) {
  return (
    <p
      className={`notice notice--${tone}`}
      role={tone === 'error' ? 'alert' : undefined}
      data-dismissable={onDismiss ? '' : undefined}
      data-compact={compact ? '' : undefined}
    >
      {Icon ? <Icon size={15} aria-hidden className="notice__icon" /> : null}
      {onDismiss ? <span className="notice__text">{children}</span> : children}
      {onDismiss ? (
        <Tooltip label="Dismiss">
          <button
            type="button"
            className="notice__dismiss"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <X size={16} aria-hidden />
          </button>
        </Tooltip>
      ) : null}
    </p>
  )
}

/**
 * What a page coming back to a checkout says about it before anything else,
 * or nothing where there is nothing to say: no checkout, or one paid, which
 * the page thanks for in its own words.
 *
 * `unpaid` and `expired` may carry the page's own sentence about what to do
 * next, and `processing` its own word for what is being confirmed — a card
 * kept is not a payment. None of the rest may invite paying again.
 */
export function ReturnedCheckoutNotice({
  state,
  unpaid,
  expired,
  processing,
}: {
  state: ReturnedCheckout
  unpaid?: ReactNode
  expired?: ReactNode
  processing?: ReactNode
}) {
  if (state === 'none' || state === 'paid') return null
  if (state === 'error') {
    return <Notice tone="error">{RETURNED_CHECKOUT_NOTICE.error}</Notice>
  }
  if (state === 'unpaid') {
    return <Notice>{unpaid ?? RETURNED_CHECKOUT_NOTICE.unpaid}</Notice>
  }
  if (state === 'expired') {
    return <Notice>{expired ?? RETURNED_CHECKOUT_NOTICE.expired}</Notice>
  }
  if (state === 'processing') {
    return <Notice>{processing ?? RETURNED_CHECKOUT_NOTICE.processing}</Notice>
  }
  return <Notice>{RETURNED_CHECKOUT_NOTICE.checking}</Notice>
}

export { AddressPlace } from './AddressPlace'
export { AliyaNameReading } from './AliyaNameReading'
export { AnswerLine } from './AnswerLine'
export { Assistant } from './Assistant'
export { Avatar, AvatarGroup, initialsOf } from './Avatar'
export { Balance } from './Balance'
export { BranchList, BranchRow } from './BranchRow'
export { Button } from './Button'
export { CardColumns } from './CardColumns'
export { CardGrid } from './CardGrid'
export { CardLink } from './CardLink'
export { Caret, foldLabel } from './Caret'
export {
  type CardStart,
  type CheckoutConfirm,
  CheckoutElements,
  returnedCheckoutId,
  showReturnedCheckout,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from './CheckoutElements'
export { Choice, type ChoiceOption } from './Choice'
export { Combobox, type ComboOption } from './Combobox'
export { Companion, type CompanionView } from './Companion'
export { Copyable } from './Copyable'
export { DefinitionList } from './DefinitionList'
export { Disclosure } from './Disclosure'
export { DrawnFrame } from './DrawnFrame'
export { EmailPreview } from './EmailPreview'
export { FactRow } from './FactRow'
export { Figure } from './Figure'
export { FigureList } from './FigureList'
export { Foot, type FootAction } from './Foot'
export { InfoCell, InfoStrip } from './InfoStrip'
export { InfoTip } from './InfoTip'
export { Menu, type MenuItem } from './Menu'
export { Meter } from './Meter'
export { Modal } from './Modal'
export { MoneyInput } from './MoneyInput'
export { OverflowGroup, useCloseOverflowGroup } from './OverflowGroup'
export { Page } from './Page'
export {
  type DocGround,
  orderedPageActions,
  PageAction,
  PageActionFor,
  PageActionGroups,
  type PageActionItem,
  PageActionProvider,
  type PageActionType,
  PageBack,
  PageCreate,
  PageDoc,
  PageInfo,
  PageTitle,
  useDocSlot,
  usePageAction,
  usePageActionItems,
  usePageBack,
  usePageDoc,
  usePageEndSlot,
  usePageInfo,
  usePagePresenceSlot,
  usePageTitle,
  usePageToolSlot,
  useSetDocSlot,
  useSetPageEndSlot,
  useSetPagePresenceSlot,
  useSetPageToolSlot,
} from './PageAction'
export { HeldAnswers, PaymentFlow, PaymentStep } from './PaymentFlow'
export { PaymentThanks } from './PaymentThanks'
export { PlanFields, type PlanShape } from './PlanFields'
export { Ask, Question } from './Question'
export { type RecordAction, RecordActions } from './RecordActions'
export { RunEditor } from './RunEditor'
export { SearchField } from './SearchField'
export { SeasonStrip } from './SeasonStrip'
export {
  type SectionAction,
  SectionActions,
  useSectionAction,
} from './SectionActions'
export { SectionNav, type SectionTab } from './SectionNav'
export { SegmentedControl } from './SegmentedControl'
export {
  Select,
  type SelectGroup,
  type SelectOption,
} from './Select'
export { Sheet } from './Sheet'
export { Statement, StatementPayByHand } from './Statement'
export {
  type Column,
  Table,
  TableCard,
  TableHeader,
  TablePagination,
  type TablePaging,
} from './Table'
export { TokenInput, type TokenSuggestion } from './TokenInput'
export { Toolbar } from './Toolbar'
export { Tooltip } from './Tooltip'
export { TotalLine } from './TotalLine'
export { Truncated } from './Truncated'
export { Underway } from './Underway'
